/**
 * ImageUploadManager - Componente Genérico para Subida de Imágenes
 * 
 * Componente adaptativo que maneja la subida de imágenes con soporte
 * mobile-first, compresión automática y preview en tiempo real.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  FaCamera, 
  FaUpload, 
  FaTrash, 
  FaImage, 
  FaSpinner, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaPlus,
  FaEdit
} from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import ImageCompressionService from '@/services/imageCompressionService';

// Hook para detectar dispositivo móvil
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const ImageUploadManager = ({
  tipo = 'antes', // 'antes', 'despues', 'proceso'
  maxImages = 5,
  required = false,
  onImagesChange,
  onUploadComplete,
  disabled = false,
  className = '',
  // Props específicos para APIs
  citaId = null, // Para imágenes previas (cita_imagenes)
  mantenimientoId = null, // Para imágenes Posterior (mantenimiento_imagenes)
  empresaId = null,
  sucursalId = null
}) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  
  const fileInputRef = useRef(null);
  const isMobile = useIsMobile();

  // Configuración de texto según el tipo
  const getTypeConfig = () => {
    const configs = {
      antes: {
        title: 'Fotos Estado Previo',
        subtitle: 'Documente el estado inicial del vehículo',
        buttonText: 'Tomar Fotos',
        dragText: 'Arrastra fotos aquí o toca para seleccionar',
        color: 'blue',
        icon: FaCamera
      },
      despues: {
        title: 'Fotos Estado Posterior',
        subtitle: 'Muestre el resultado del trabajo realizado',
        buttonText: 'Tomar Fotos',
        dragText: 'Arrastra fotos del resultado aquí',
        color: 'green',
        icon: FaCheckCircle
      },
      proceso: {
        title: 'Fotos del Proceso',
        subtitle: 'Documente el proceso de trabajo',
        buttonText: 'Tomar Fotos',
        dragText: 'Fotos del trabajo en progreso',
        color: 'purple',
        icon: FaImage
      }
    };
    return configs[tipo] || configs.antes;
  };

  const config = getTypeConfig();

  // Manejar selección de archivos
  const handleFileSelect = useCallback(async (files) => {
    if (disabled) return;
    
    const fileArray = Array.from(files);
    
    // Validar límite de imágenes
    if (images.length + fileArray.length > maxImages) {
      toast.error(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }

    setUploading(true);
    const newImages = [];
    
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fileId = `${Date.now()}_${i}`;
        
        // Generar nombre único para el archivo
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        const fileExtension = file.name.split('.').pop() || 'jpg';
        const uniqueFileName = `${tipo}_${timestamp}_${randomSuffix}.${fileExtension}`;
        
        // Actualizar progreso
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'compressing', progress: 0 }
        }));

        try {
          // Comprimir imagen
          const compressionResult = await ImageCompressionService.compressImage(file);
          
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { status: 'compressed', progress: 50 }
          }));

          // Generar thumbnail
          const thumbnailResult = await ImageCompressionService.generateThumbnail(file);
          
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { status: 'ready', progress: 100 }
          }));

          // Crear preview URL
          const previewUrl = URL.createObjectURL(compressionResult.compressedFile);
          const thumbnailUrl = URL.createObjectURL(thumbnailResult.thumbnailFile);

          const imageData = {
            id: fileId,
            originalFile: file,
            compressedFile: compressionResult.compressedFile,
            thumbnailFile: thumbnailResult.thumbnailFile,
            previewUrl,
            thumbnailUrl,
            descripcion: '',
            uniqueFileName,
            orden: images.length + newImages.length,
            metadata: {
              originalSize: compressionResult.originalSize,
              compressedSize: compressionResult.compressedSize,
              compressionRatio: compressionResult.compressionRatio,
              dimensions: compressionResult.dimensions
            },
            status: 'ready',
            uploaded: false
          };

          newImages.push(imageData);

        } catch (error) {
          console.error(`Error procesando ${file.name}:`, error);
          toast.error(`Error procesando ${file.name}: ${error.message}`);
          
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { status: 'error', progress: 0, error: error.message }
          }));
        }
      }

      // Actualizar estado de imágenes
      const updatedImages = [...images, ...newImages];
      setImages(updatedImages);
      
      // Notificar cambios al componente padre
      if (onImagesChange) {
        onImagesChange(updatedImages);
      }

      if (newImages.length > 0) {
        toast.success(`${newImages.length} imagen${newImages.length > 1 ? 'es' : ''} agregada${newImages.length > 1 ? 's' : ''}`);
      }

    } catch (error) {
      console.error('Error general procesando imágenes:', error);
      toast.error('Error procesando las imágenes');
    } finally {
      setUploading(false);
      // Limpiar progreso después de un tiempo
      setTimeout(() => {
        setUploadProgress({});
      }, 2000);
    }
  }, [images, maxImages, disabled, onImagesChange]);

  // Manejar input de archivo
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Limpiar input para permitir seleccionar el mismo archivo
    e.target.value = '';
  };

  // Manejar drag & drop (solo desktop)
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled && !isMobile) {
      setDragOver(true);
    }
  }, [disabled, isMobile]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled || isMobile) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  }, [disabled, isMobile, handleFileSelect]);

  // Eliminar imagen
  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    
    // Limpiar URLs de objeto para liberar memoria
    const imageToRemove = images.find(img => img.id === imageId);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.previewUrl);
      URL.revokeObjectURL(imageToRemove.thumbnailUrl);
    }
    
    setImages(updatedImages);
    
    if (onImagesChange) {
      onImagesChange(updatedImages);
    }
    
    toast.success('Imagen eliminada');
  };

  // Actualizar descripción
  const handleUpdateDescription = (imageId, descripcion) => {
    const updatedImages = images.map(img => 
      img.id === imageId ? { ...img, descripcion } : img
    );
    setImages(updatedImages);
    
    if (onImagesChange) {
      onImagesChange(updatedImages);
    }
  };

  // Abrir cámara/selector de archivos
  const handleOpenCamera = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  // Cleanup de URLs al desmontar
  useEffect(() => {
    return () => {
      images.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
        URL.revokeObjectURL(img.thumbnailUrl);
      });
    };
  }, []);

  // Renderizado condicional para móvil vs desktop
  if (isMobile) {
    return (
      <div className={`space-y-4 ${className}`}>
        {/* Header móvil */}
        <div className="text-center">
          <h3 className="text-white font-medium mb-1">{config.title}</h3>
          <p className="text-gray-400 text-sm">{config.subtitle}</p>
          <span className="text-xs text-gray-500">
            {images.length}/{maxImages} imágenes
          </span>
        </div>

        {/* Botón de captura móvil */}
        <button
          type="button"
          onClick={handleOpenCamera}
          disabled={disabled || uploading || images.length >= maxImages}
          className={`w-full py-4 px-6 rounded-lg flex items-center justify-center text-lg font-medium transition-all ${
            disabled || images.length >= maxImages
              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
              : `bg-${config.color}-600 hover:bg-${config.color}-700 text-white active:scale-95`
          }`}
        >
          {uploading ? (
            <>
              <FaSpinner className="animate-spin mr-3" size={20} />
              Procesando...
            </>
          ) : (
            <>
              <config.icon className="mr-3" size={20} />
              {config.buttonText}
            </>
          )}
        </button>

        {/* Grid de imágenes móvil */}
        {images.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {images.map((image) => (
              <div key={image.id} className="relative">
                <div className="aspect-square bg-gray-800 rounded-lg overflow-hidden">
                  <img
                    src={image.previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Botón de eliminar fijo en esquina superior derecha */}
                  <button
                    onClick={() => handleRemoveImage(image.id)}
                    className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full transition-colors z-10"
                  >
                    <FaTrash size={10} />
                  </button>

                  {/* Indicador de estado */}
                  {uploadProgress[image.id] && (
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="bg-black bg-opacity-50 rounded px-2 py-1">
                        <div className="text-xs text-white">
                          {uploadProgress[image.id].status === 'compressing' && 'Comprimiendo...'}
                          {uploadProgress[image.id].status === 'compressed' && 'Generando thumbnail...'}
                          {uploadProgress[image.id].status === 'ready' && '✓ Listo'}
                          {uploadProgress[image.id].status === 'error' && '⚠ Error'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Campo de descripción móvil */}
                <input
                  type="text"
                  placeholder="Descripción opcional..."
                  value={image.descripcion}
                  onChange={(e) => handleUpdateDescription(image.id, e.target.value)}
                  className="mt-2 w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
                />
              </div>
            ))}
          </div>
        )}

        {/* Input de archivo oculto */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    );
  }

  // Renderizado para Desktop
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header desktop */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-white font-medium">{config.title}</h3>
          <p className="text-gray-400 text-sm">{config.subtitle}</p>
        </div>
        <span className="text-sm text-gray-500">
          {images.length}/{maxImages} imágenes
        </span>
      </div>

      {/* Zona de drop desktop */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleOpenCamera}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${dragOver 
            ? `border-${config.color}-400 bg-${config.color}-900 bg-opacity-20` 
            : 'border-gray-600 hover:border-gray-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        {uploading ? (
          <div className="space-y-3">
            <FaSpinner className="animate-spin mx-auto text-blue-400" size={32} />
            <p className="text-white">Procesando imágenes...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <config.icon className={`mx-auto text-${config.color}-400`} size={32} />
            <div>
              <p className="text-white font-medium">{config.dragText}</p>
              <p className="text-gray-400 text-sm mt-1">
                JPG, PNG, WEBP • Máximo 5MB por imagen
              </p>
            </div>
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 bg-${config.color}-600 hover:bg-${config.color}-700 text-white rounded-lg transition-colors`}
            >
              <FaUpload className="mr-2" size={14} />
              Seleccionar Archivos
            </button>
          </div>
        )}
      </div>

      {/* Grid de imágenes desktop */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div key={image.id} className="space-y-2">
              <div className="relative group aspect-square bg-gray-800 rounded-lg overflow-hidden">
                <img
                  src={image.previewUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay con controles */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(image.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 hover:bg-red-700 text-white p-2 rounded-full"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>

                {/* Indicador de estado */}
                {uploadProgress[image.id] && (
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="bg-black bg-opacity-75 rounded px-2 py-1">
                      <div className="text-xs text-white text-center">
                        {uploadProgress[image.id].status === 'compressing' && 'Comprimiendo...'}
                        {uploadProgress[image.id].status === 'compressed' && 'Thumbnail...'}
                        {uploadProgress[image.id].status === 'ready' && '✓ Listo'}
                        {uploadProgress[image.id].status === 'error' && '⚠ Error'}
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-1 mt-1">
                        <div 
                          className={`bg-${config.color}-400 h-1 rounded-full transition-all`}
                          style={{ width: `${uploadProgress[image.id].progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Campo de descripción desktop */}
              <input
                type="text"
                placeholder="Descripción opcional..."
                value={image.descripcion}
                onChange={(e) => handleUpdateDescription(image.id, e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Info de compresión */}
              <div className="text-xs text-gray-500">
                {image.metadata.compressionRatio}% comprimido • {Math.round(image.metadata.compressedSize / 1024)}KB
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Input de archivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Mensaje de validación */}
      {required && images.length === 0 && (
        <div className="flex items-center text-yellow-400 text-sm">
          <FaExclamationTriangle className="mr-2" size={14} />
          Este campo es requerido
        </div>
      )}
    </div>
  );
};

export default ImageUploadManager;